import { Resolver } from "node:dns/promises";
import { request, type RequestOptions } from "node:https";
import { BlockList, isIP } from "node:net";
import type { ClientRequest, IncomingMessage } from "node:http";

const DEADLINE_MS = 8_000;
const MAX_BODY_BYTES = 64 * 1024;
const MAX_URL_CHARS = 2_048;

export type ProtectedResourceMetadataFailureReason =
  | "invalid_resource_url"
  | "unsafe_destination"
  | "dns_failure"
  | "timeout"
  | "http_status"
  | "redirect_refused"
  | "invalid_content_type"
  | "response_too_large"
  | "invalid_metadata"
  | "resource_mismatch"
  | "request_failed";

export type ProtectedResourceMetadataResult =
  | {
      ok: true;
      resource: string;
      metadataUrl: string;
      authorizationServers?: string[];
      scopesSupported?: string[];
    }
  | { ok: false; reason: ProtectedResourceMetadataFailureReason; message: string };

const FAILURE_MESSAGES: Record<ProtectedResourceMetadataFailureReason, string> = {
  invalid_resource_url:
    "An explicit public HTTPS resource URL without credentials, query, fragment or a non-443 port is required.",
  unsafe_destination: "Metadata discovery refused a non-public or unsupported destination address.",
  dns_failure: "Metadata discovery could not validate all destination DNS addresses.",
  timeout: "Metadata discovery exceeded its eight-second deadline.",
  http_status: "The protected-resource metadata endpoint did not return HTTP 200.",
  redirect_refused:
    "The protected-resource metadata endpoint returned a redirect; redirects are not followed.",
  invalid_content_type:
    "The protected-resource metadata response was not uncompressed application JSON.",
  response_too_large: "The protected-resource metadata response exceeded 64 KiB.",
  invalid_metadata:
    "The protected-resource metadata was malformed or exceeded supported field bounds.",
  resource_mismatch:
    "The metadata resource identifier did not exactly match the validated requested resource.",
  request_failed: "The protected-resource metadata HTTPS request could not be completed.",
};

function failure(reason: ProtectedResourceMetadataFailureReason): ProtectedResourceMetadataResult {
  return { ok: false, reason, message: FAILURE_MESSAGES[reason] };
}

const blockedV4 = new BlockList();
for (const [network, prefix] of [
  ["0.0.0.0", 8],
  ["10.0.0.0", 8],
  ["100.64.0.0", 10],
  ["127.0.0.0", 8],
  ["169.254.0.0", 16],
  ["172.16.0.0", 12],
  ["192.0.0.0", 24],
  ["192.0.2.0", 24],
  ["192.88.99.0", 24],
  ["192.168.0.0", 16],
  ["198.18.0.0", 15],
  ["198.51.100.0", 24],
  ["203.0.113.0", 24],
  ["224.0.0.0", 4],
  ["240.0.0.0", 4],
] as const)
  blockedV4.addSubnet(network, prefix, "ipv4");
const globalV6 = new BlockList();
globalV6.addSubnet("2000::", 3, "ipv6");
const blockedV6 = new BlockList();
for (const [network, prefix] of [
  ["2001::", 23],
  ["2001:db8::", 32],
  ["2002::", 16],
  ["3fff::", 20],
] as const)
  blockedV6.addSubnet(network, prefix, "ipv6");

function publicAddress(address: string): boolean {
  const family = isIP(address);
  if (family === 4) return !blockedV4.check(address, "ipv4");
  if (family !== 6 || !globalV6.check(address, "ipv6") || blockedV6.check(address, "ipv6"))
    return false;
  // Conservatively refuse ISATAP interface identifiers, in addition to the
  // mapped, compatible, NAT64, Teredo and 6to4 ranges excluded above.
  const canonical = new URL(`https://[${address}]/`).hostname.slice(1, -1);
  const [left = "", right = ""] = canonical.split("::");
  const start = left ? left.split(":") : [];
  const end = right ? right.split(":") : [];
  const parts = canonical.includes("::")
    ? [...start, ...Array<string>(8 - start.length - end.length).fill("0"), ...end]
    : start;
  return !([0, 0x200].includes(parseInt(parts[4]!, 16)) && parseInt(parts[5]!, 16) === 0x5efe);
}

function validateUrl(value: string): URL | null {
  if (
    typeof value !== "string" ||
    value.length > MAX_URL_CHARS ||
    !/^https:\/\//i.test(value) ||
    /[^\x21-\x7e]|[\\?#]/.test(value)
  )
    return null;
  try {
    const url = new URL(value);
    if (
      url.protocol !== "https:" ||
      url.username ||
      url.password ||
      url.port ||
      url.search ||
      url.hash
    )
      return null;
    const authority = value.slice(value.indexOf("://") + 3).split("/")[0]!;
    if (authority.includes("%") || authority.includes("@")) return null;
    const host = url.hostname.replace(/\.$/, "");
    if (
      !host ||
      host.endsWith(".") ||
      /(?:^|\.)(?:localhost|local|internal|home|lan|invalid|test)$/.test(host)
    )
      return null;
    const address = host.startsWith("[") ? host.slice(1, -1) : host;
    // WHATWG URL accepts legacy decimal/octal/hex IPv4 spellings. Refuse
    // those alternate spellings instead of silently reinterpreting a host.
    if (isIP(address) === 4 && authority.replace(/:443$/, "").replace(/\.$/, "") !== host)
      return null;
    if (
      !isIP(address) &&
      (!host.includes(".") ||
        !host.split(".").every((label) => /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(label)))
    )
      return null;
    url.hostname = host;
    return url;
  } catch {
    return null;
  }
}

function metadataResult(
  text: string,
  resource: string,
  metadataUrl: string,
): ProtectedResourceMetadataResult {
  try {
    const value: unknown = JSON.parse(text);
    if (!value || typeof value !== "object" || Array.isArray(value))
      return failure("invalid_metadata");
    const record = value as Record<string, unknown>;
    if (typeof record.resource !== "string") return failure("invalid_metadata");
    if (record.resource !== resource) return failure("resource_mismatch");
    const result: ProtectedResourceMetadataResult = { ok: true, resource, metadataUrl };
    if (record.authorization_servers !== undefined) {
      if (
        !Array.isArray(record.authorization_servers) ||
        record.authorization_servers.length > 20 ||
        !record.authorization_servers.every((item: unknown) => {
          if (typeof item !== "string") return false;
          const issuer = validateUrl(item);
          if (!issuer) return false;
          const address = issuer.hostname.replace(/^\[|\]$/g, "");
          return !isIP(address) || publicAddress(address);
        })
      )
        return failure("invalid_metadata");
      result.authorizationServers = record.authorization_servers as string[];
    }
    if (record.scopes_supported !== undefined) {
      if (
        !Array.isArray(record.scopes_supported) ||
        record.scopes_supported.length > 100 ||
        !record.scopes_supported.every(
          (item: unknown) =>
            typeof item === "string" &&
            item.length <= 256 &&
            /^[\x21\x23-\x5b\x5d-\x7e]+$/.test(item),
        )
      )
        return failure("invalid_metadata");
      result.scopesSupported = record.scopes_supported as string[];
    }
    return result;
  } catch {
    return failure("invalid_metadata");
  }
}

/** One public RFC 9728 metadata GET. No credentials, redirects, issuer fallback or auth changes. */
export async function discoverProtectedResourceMetadata(
  resourceUrl: string,
): Promise<ProtectedResourceMetadataResult> {
  const resource = validateUrl(resourceUrl);
  if (!resource) return failure("invalid_resource_url");
  const hostname = resource.hostname.replace(/^\[|\]$/g, "");
  const literalFamily = isIP(hostname);
  if (literalFamily && !publicAddress(hostname)) return failure("unsafe_destination");
  const metadataUrl = `${resource.origin}/.well-known/oauth-protected-resource${resource.pathname === "/" ? "" : resource.pathname}`;
  const resolver = new Resolver();
  let outgoing: ClientRequest | undefined;
  let incoming: IncomingMessage | undefined;
  let finished = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  return new Promise((resolve) => {
    const finish = (result: ProtectedResourceMetadataResult): void => {
      if (finished) return;
      finished = true;
      if (timer) clearTimeout(timer);
      resolver.cancel();
      incoming?.destroy();
      outgoing?.destroy();
      resolve(result);
    };
    timer = setTimeout(() => finish(failure("timeout")), DEADLINE_MS);
    void (async () => {
      let addresses: { address: string; family: 4 | 6 }[];
      if (literalFamily === 4 || literalFamily === 6)
        addresses = [{ address: hostname, family: literalFamily }];
      else {
        const answers = await Promise.allSettled([
          resolver.resolve4(hostname),
          resolver.resolve6(hostname),
        ]);
        if (finished) return;
        addresses = [];
        for (const [index, answer] of answers.entries()) {
          if (answer.status === "fulfilled")
            addresses.push(
              ...answer.value.map((address): { address: string; family: 4 | 6 } => ({
                address,
                family: index === 0 ? 4 : 6,
              })),
            );
          else if (!["ENODATA"].includes(String((answer.reason as NodeJS.ErrnoException)?.code))) {
            finish(failure("dns_failure"));
            return;
          }
        }
        if (!addresses.length) {
          finish(failure("dns_failure"));
          return;
        }
        if (
          addresses.length > 64 ||
          addresses.some(
            ({ address, family }) => isIP(address) !== family || !publicAddress(address),
          )
        ) {
          finish(failure("unsafe_destination"));
          return;
        }
      }
      if (finished) return;
      const selected = addresses[0]!;
      const lookup: NonNullable<RequestOptions["lookup"]> = (requestedHost, options, callback) => {
        if (requestedHost !== hostname) {
          callback(new Error("Unexpected metadata lookup hostname."), "", selected.family);
          return;
        }
        if (typeof options === "object" && options.all) callback(null, [selected]);
        else callback(null, selected.address, selected.family);
      };
      outgoing = request(
        new URL(metadataUrl),
        {
          method: "GET",
          agent: false,
          lookup,
          family: selected.family,
          rejectUnauthorized: true,
          ...(literalFamily ? {} : { servername: hostname }),
          headers: { Accept: "application/json", "Accept-Encoding": "identity" },
        },
        (response) => {
          incoming = response;
          if (finished) {
            response.destroy();
            return;
          }
          if (response.statusCode && response.statusCode >= 300 && response.statusCode < 400) {
            finish(failure("redirect_refused"));
            return;
          }
          if (response.statusCode !== 200) {
            finish(failure("http_status"));
            return;
          }
          const type = response.headers["content-type"];
          const encoding = response.headers["content-encoding"];
          if (
            typeof type !== "string" ||
            !/^application\/(?:json|[a-z0-9!#$&^_.+-]+\+json)(?:\s*;[^\r\n]*)?$/i.test(type) ||
            (encoding !== undefined && encoding !== "identity")
          ) {
            finish(failure("invalid_content_type"));
            return;
          }
          const length = response.headers["content-length"];
          if (
            typeof length === "string" &&
            /^\d+$/.test(length) &&
            Number(length) > MAX_BODY_BYTES
          ) {
            finish(failure("response_too_large"));
            return;
          }
          const chunks: Buffer[] = [];
          let bytes = 0;
          response.on("data", (chunk: Buffer) => {
            if (finished) return;
            bytes += chunk.length;
            if (bytes > MAX_BODY_BYTES) {
              finish(failure("response_too_large"));
              return;
            }
            chunks.push(chunk);
          });
          response.once("error", () => finish(failure("request_failed")));
          response.once("aborted", () => finish(failure("request_failed")));
          response.once("end", () => {
            if (finished) return;
            try {
              finish(
                metadataResult(
                  new TextDecoder("utf-8", { fatal: true }).decode(Buffer.concat(chunks)),
                  resource.href,
                  metadataUrl,
                ),
              );
            } catch {
              finish(failure("invalid_metadata"));
            }
          });
        },
      );
      outgoing.once("error", () => finish(failure("request_failed")));
      outgoing.end();
    })().catch(() => finish(failure("request_failed")));
  });
}
